import React from 'react';

/**
 * Generic React error boundary. Wrap pages prone to runtime crashes so a
 * single bad email/record doesn't white-screen the whole SPA.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to console so devtools captures stack.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || 'Something went wrong.';
      return (
        <div
          className="m-6 p-6 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20"
          data-testid="error-boundary-fallback"
        >
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-red-900 dark:text-red-100">
                {this.props.title || 'This section crashed'}
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{msg}</p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                Other parts of the app are unaffected. Try reloading this section.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={this.handleReset}
                  className="px-3 py-1.5 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-md"
                  data-testid="error-boundary-retry-btn"
                >
                  Retry
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-3 py-1.5 text-sm font-medium bg-white hover:bg-red-50 text-red-700 border border-red-300 rounded-md dark:bg-transparent dark:text-red-300 dark:border-red-700"
                  data-testid="error-boundary-reload-btn"
                >
                  Reload page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
